/**
 * financiamento.js — Simulação de Financiamento
 *
 * - Carrega veículos disponíveis via fetchVeiculosDisponiveis() e popula o <select>
 * - Campos: Nome, WhatsApp (máscara), CPF (máscara + validação), Veículo, Mensagem
 * - Envio via registrarLeadPublico() com origem: 'Financiamento' e veiculo_id do select
 * - Validação inline + live-clear
 * - Estado de sucesso com reset
 */

import { registrarLeadPublico } from '../services/leads.js';
import { fetchTodosVeiculos } from '../services/veiculos.js';

// ── Helpers ────────────────────────────────────────────────────
const $ = id => document.getElementById(id);

// ── Máscara de telefone ────────────────────────────────────────
function mascaraTelefone(input) {
  input.addEventListener('input', () => {
    let v = input.value.replace(/\D/g, '').slice(0, 11);
    if (v.length > 6)      v = `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
    else if (v.length > 2) v = `(${v.slice(0, 2)}) ${v.slice(2)}`;
    else if (v.length > 0) v = `(${v}`;
    input.value = v;
  });
}

// ── Máscara de CPF (000.000.000-00) ───────────────────────────
function mascaraCpf(input) {
  input.addEventListener('input', () => {
    let v = input.value.replace(/\D/g, '').slice(0, 11);
    if (v.length > 9)      v = `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}`;
    else if (v.length > 6) v = `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6)}`;
    else if (v.length > 3) v = `${v.slice(0, 3)}.${v.slice(3)}`;
    input.value = v;
  });
}

// ── Validação CPF (algoritmo dos dígitos verificadores) ───────
function cpfValido(cpf) {
  const n = cpf.replace(/\D/g, '');
  if (n.length !== 11 || /^(\d)\1{10}$/.test(n)) return false;
  let soma = 0;
  for (let i = 0; i < 9; i++) soma += Number(n[i]) * (10 - i);
  let r = (soma * 10) % 11;
  if (r === 10 || r === 11) r = 0;
  if (r !== Number(n[9])) return false;
  soma = 0;
  for (let i = 0; i < 10; i++) soma += Number(n[i]) * (11 - i);
  r = (soma * 10) % 11;
  if (r === 10 || r === 11) r = 0;
  return r === Number(n[10]);
}

// ── Validação do formulário ────────────────────────────────────
function setError(inputId, errorId, msg) {
  const el  = $(inputId);
  const err = $(errorId);
  if (!el || !err) return;
  if (msg) {
    el.classList.add('vender-invalid');
    err.textContent = msg;
  } else {
    el.classList.remove('vender-invalid');
    err.textContent = '';
  }
}

const CAMPOS = ['fin-nome', 'fin-telefone', 'fin-cpf', 'fin-veiculo'];

function clearAllErrors() {
  CAMPOS.forEach(id => $(id)?.classList.remove('vender-invalid'));
  ['ferr-nome', 'ferr-telefone', 'ferr-cpf', 'ferr-veiculo'].forEach(id => {
    const el = $(id); if (el) el.textContent = '';
  });
  $('ferr-global')?.classList.remove('show');
}

function validate() {
  clearAllErrors();
  let valid = true;

  const nome     = $('fin-nome')?.value.trim()     ?? '';
  const telefone = $('fin-telefone')?.value.trim() ?? '';
  const cpf      = $('fin-cpf')?.value.trim()      ?? '';
  const veiculo  = $('fin-veiculo')?.value         ?? '';

  if (!nome) {
    setError('fin-nome', 'ferr-nome', 'Nome é obrigatório.');
    valid = false;
  }

  if (!telefone.replace(/\D/g, '') || telefone.replace(/\D/g, '').length < 10) {
    setError('fin-telefone', 'ferr-telefone', 'Informe um WhatsApp válido.');
    valid = false;
  }

  const cpfRaw = cpf.replace(/\D/g, '');
  if (!cpfRaw || cpfRaw.length !== 11) {
    setError('fin-cpf', 'ferr-cpf', 'Informe o CPF completo.');
    valid = false;
  } else if (!cpfValido(cpf)) {
    setError('fin-cpf', 'ferr-cpf', 'CPF inválido.');
    valid = false;
  }

  if (!veiculo) {
    setError('fin-veiculo', 'ferr-veiculo', 'Selecione o veículo de interesse.');
    valid = false;
  }

  return valid;
}

// ── Link WhatsApp ──────────────────────────────────────────────
export function atualizarWhatsappFinanciamento(numero) {
  const btn = $('btn-fin-whatsapp');
  if (!btn || !numero) return;
  const clean = numero.replace(/\D/g, '');
  btn.href = `https://wa.me/55${clean}?text=${encodeURIComponent('Olá! Gostaria de fazer a simulação de um financiamento.')}`;
}

// ── Carregar veículos no select ────────────────────────────────
async function carregarVeiculos() {
  const select = $('fin-veiculo');
  if (!select) return;

  select.innerHTML = '<option value="">Carregando veículos...</option>';
  select.disabled = true;

  const { data, error } = await fetchTodosVeiculos();

  if (error || !data?.length) {
    select.innerHTML = '<option value="">Nenhum veículo disponível</option>';
    return;
  }

  select.innerHTML = '<option value="">Selecione o veículo desejado</option>';
  data.forEach(v => {
    const nome  = `${v.nome ?? ''} ${v.modelo ?? ''}`.trim();
    const ano   = v.ano_modelo ?? v.ano_fabricacao ?? '';
    const preco = v.preco_venda?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }) ?? '';
    const label = [nome, ano, preco].filter(Boolean).join(' · ');
    const opt   = document.createElement('option');
    opt.value       = v.id;
    opt.textContent = label;
    select.appendChild(opt);
  });

  select.disabled = false;
}

// ── Estado de sucesso / reset ──────────────────────────────────
function mostrarSucesso() {
  $('fin-form-state')?.setAttribute('hidden', '');
  $('fin-sucesso')?.classList.remove('vender-sucesso--hidden');
}

function resetarFormulario() {
  $('form-financiamento')?.reset();
  clearAllErrors();
  const btn = $('btn-fin-submit');
  if (btn) { btn.disabled = false; btn.textContent = 'Solicitar simulação'; }
  $('fin-form-state')?.removeAttribute('hidden');
  $('fin-sucesso')?.classList.add('vender-sucesso--hidden');
}

// ── Submit ─────────────────────────────────────────────────────
async function handleSubmit(e) {
  e.preventDefault();
  if (!validate()) return;

  const btn      = $('btn-fin-submit');
  const nome     = $('fin-nome').value.trim();
  const tel      = $('fin-telefone').value.replace(/\D/g, '');
  const cpf      = $('fin-cpf').value.trim();
  const veiculoId = $('fin-veiculo').value;
  const msg      = $('fin-mensagem')?.value.trim() || null;

  btn.disabled    = true;
  btn.textContent = 'Enviando...';

  // Ocultar erro global anterior
  $('ferr-global')?.classList.remove('show');

  const payload = {
    nome,
    telefone:   tel,
    cpf,
    veiculo_id: veiculoId,
    mensagem:   msg,
    origem:     'Financiamento',
  };

  const { data, error } = await registrarLeadPublico(payload);

  if (error || !data?.ok) {
    console.error('[financiamento] Erro ao enviar lead:', error || data);
    btn.disabled    = false;
    btn.textContent = 'Solicitar simulação';
    
    // Feedback visual elegante em vez do alert
    const globalError = $('ferr-global');
    const globalErrorText = $('ferr-global-text');
    if (globalError && globalErrorText) {
      globalErrorText.textContent = error?.message || data?.error || 'Ocorreu um erro ao enviar. Por favor, tente novamente ou fale pelo WhatsApp.';
      globalError.classList.add('show');
    }
    return;
  }

  mostrarSucesso();
}

// ── Inicialização ──────────────────────────────────────────────
export function initFinanciamento() {
  const form = $('form-financiamento');
  if (!form) return;

  // Máscaras
  mascaraTelefone($('fin-telefone'));
  mascaraCpf($('fin-cpf'));

  // Live-clear de erros
  CAMPOS.forEach(id => {
    $(id)?.addEventListener('input', () => {
      $(id)?.classList.remove('vender-invalid');
      const errEl = $(`ferr-${id.replace('fin-', '')}`);
      if (errEl) errEl.textContent = '';
    });
    $(id)?.addEventListener('change', () => {
      $(id)?.classList.remove('vender-invalid');
      const errEl = $(`ferr-${id.replace('fin-', '')}`);
      if (errEl) errEl.textContent = '';
    });
  });

  form.addEventListener('submit', handleSubmit);
  $('btn-fin-nova')?.addEventListener('click', resetarFormulario);

  // Carrega veículos no select
  carregarVeiculos();
}
